import React, { useState, useEffect, useMemo } from 'react';
import { usePortfolio, useQuotes } from '../../logic/imports';
import { PositionComp } from './PositionComp';

let hasPrintedWarnings = false;

export const PositionsPanel: React.FC = () => {
    const { positions } = usePortfolio();
    const { quotes, requestQuote, releaseQuote } = useQuotes();
    const [spyPrice, setSpyPrice] = useState<number>(0);

    const tickerFilter: string[] = [];
    const useMaxDTEFilter = false;

    if(!hasPrintedWarnings)
    {
        if(tickerFilter.length === 0)
            console.warn('Allowing all tickers for testing');
        if(!useMaxDTEFilter)
            console.warn('Allowing all DTEs for testing');
        hasPrintedWarnings = true;
    }

    // Subscribe to SPY quote on mount, unsubscribe on unmount
    useEffect(() => {
        const subId = crypto.randomUUID().substring(0, 3);

        requestQuote('SPY', `PositionsPanel-${subId}`);

        return () => {
            releaseQuote('SPY', `PositionsPanel-${subId}`);
        };
    }, []);

    // Update SPY price from quotes
    useEffect(() => {
        const spyQuote = quotes.find(q => q.instrument.symbol === 'SPY');
        if (spyQuote) {
            setSpyPrice(spyQuote.last);
        }
    }, [quotes]);

    // Process positions with useMemo to prevent unnecessary recalculation
    const { tickerPositions } = useMemo(() => {
        if (!positions || positions.size === 0) {
            return { tickerPositions: [] };
        }

        // Helper to extract expiration date from symbol (YYMMDD format)
        const extractDate = (symbol: string) => {
            const match = symbol.match(/(\d{6})[CP]/);
            return match ? match[1] : '';
        };

        // Helper to calculate DTE
        const calculateDTE = (dateStr: string): number => {
            if (!dateStr) return Infinity;
            const yy = parseInt(dateStr.substring(0, 2));
            const mm = parseInt(dateStr.substring(2, 4));
            const dd = parseInt(dateStr.substring(4, 6));
            const expDate = new Date(2000 + yy, mm - 1, dd);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return Math.ceil((expDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        };

        // TODO move above helpers outside useMemo into utils or something else?

        // Determine max DTE based on day of week (0=Sunday, 5=Friday)
        const today = new Date();
        const dayOfWeek = today.getDay();
        const maxDTE = dayOfWeek === 5 ? 3 : 1; // Friday=5, allow 3DTE; others allow 1DTE

        const filteredPositions: any[] = [];

        positions.forEach((position) => {
            if (!position.option.expiration 
                || !position.option.strike
                || !position.option.type
                || !position.option.underlying
            ) {
                // Not an option position
                console.warn('[PositionsPanel] Skipping non-option position:', position);
                return;
            }

            // Extract ticker symbol (letters before first digit)
            const underlying = position.option.underlying.symbol;
            if (!underlying) { 
                console.warn('[PositionsPanel] Position missing underlying symbol:', position);
                return;
            }

            // Only process SPY
            if (tickerFilter.length > 0 && !tickerFilter.includes(underlying)) return;

            // Filter by DTE
            const dateStr = extractDate(position.option.symbol);
            const dte = calculateDTE(dateStr);
            if (useMaxDTEFilter && dte > maxDTE) return;

            // Calculate distance from current price (use 0 if spyPrice not yet available)
            const distance = Math.abs(position.option.strike - (spyPrice || 0));

            const posWithMeta = {
                ...position,    
                distance
            };

            filteredPositions.push(posWithMeta);
        });

        // Sort by DTE ascending (lowest DTE first), then by strike price ascending
        filteredPositions.sort((a, b) => {
            const dateA = extractDate(a.instrument.symbol);
            const dateB = extractDate(b.instrument.symbol);

            if (!dateA || !dateB) return 0;

            // Parse YYMMDD to Date
            const yy = parseInt(dateA.substring(0, 2));
            const mm = parseInt(dateA.substring(2, 4));
            const dd = parseInt(dateA.substring(4, 6));
            const expA = new Date(2000 + yy, mm - 1, dd);

            const yyB = parseInt(dateB.substring(0, 2));
            const mmB = parseInt(dateB.substring(2, 4));
            const ddB = parseInt(dateB.substring(4, 6));
            const expB = new Date(2000 + yyB, mmB - 1, ddB);

            // First sort by DTE ascending (closest expiration first)
            const dteDiff = expA.getTime() - expB.getTime();
            if (dteDiff !== 0) return dteDiff;

            // If DTE is the same, sort by strike price ascending
            const strikeA = a.instrument.optionStrike ?? 0;
            const strikeB = b.instrument.optionStrike ?? 0;
            return strikeA + strikeB;
        });

        return { tickerPositions: filteredPositions };
    }, [positions, spyPrice]);
    
    return (
        <div className="panel">
            <div className="panel-header">Positions</div>

            <div>
                {tickerPositions.length === 0 ? (
                    <div style={{ color: '#829aac', textAlign: 'center', fontStyle: 'italic' }}>
                        No Open Positions
                    </div>
                ) : (
                    <div className="grid" style={{
                        gridTemplateColumns: '1fr',
                        gap: '10px'
                    }}>
                        {tickerPositions.map((pos) => (
                            <PositionComp key={`${pos.id}`} position={pos} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
